import React, { useState } from 'react';
import {
    Box, Typography, Container, Grid, Paper, Button, useTheme, Card, CardContent, Divider, Select, MenuItem, FormControl, InputLabel, Chip, Tooltip, LinearProgress,
    // FIX 1: Added IconButton
    IconButton,
} from '@mui/material';
import {
    Assessment as ReportsIcon, TrendingUp as TrendIcon, CloudDownload as DownloadIcon, FilterList as FilterIcon, Schedule as ScheduleIcon, Refresh as RefreshIcon, PeopleAlt as PeopleIcon, AttachMoney as MoneyIcon
} from '@mui/icons-material';

// --- MOCK Data ---
const mockReportData = {
    employeeMetrics: [
        { name: 'Active', value: 92, color: '#388E3C' }, // Green
        { name: 'On Leave', value: 5, color: '#FF9800' }, // Orange
        { name: 'Terminated', value: 3, color: '#D32F2F' }, // Red
    ],
    financeSummary: {
        budgetUtilization: 75, // in %
        variance: -5.2, // in %
    },
    trainingCompletion: [
        { course: 'Security Policy', completed: 85 },
        { course: 'Compliance 101', completed: 95 },
        { course: 'Leadership', completed: 45 },
    ]
};

// --- Sub-Component: Employee Distribution Chart (Simulated) ---
const EmployeeDistributionChart = ({ data, total, theme }) => {
    return (
        <Box sx={{ height: 180, display: 'flex', flexDirection: 'column', justifyContent: 'space-around', p: 2 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>Current Headcount Distribution</Typography>
            {data.map(item => (
                <Box key={item.name} sx={{ my: 1 }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                        <Typography variant="body2">{item.value}%</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={item.value}
                        sx={{ height: 10, borderRadius: 5, bgcolor: theme.palette.grey[300], '& .MuiLinearProgress-bar': { bgcolor: item.color } }}
                    />
                </Box>
            ))}
        </Box>
    );
};

// --- Sub-Component: Training Progress Card ---
// This is a component that renders the content of the Training Progress card
const TrainingProgressCard = ({ data, theme }) => {
    return (
        <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
                <ReportsIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={700}>Mandatory Training Progress</Typography>
            </Box>
            {data.map((item, index) => (
                <Box key={index} sx={{ my: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{item.course}</Typography>
                        <Typography variant="body2" fontWeight={700} color={item.completed < 50 ? 'error.main' : 'success.main'}>{item.completed}%</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={item.completed}
                        sx={{ height: 8, borderRadius: 4, transition: 'width 1s ease-out' }}
                        color={item.completed < 50 ? 'error' : 'success'}
                    />
                </Box>
            ))}
            <Button size="small" variant="text" fullWidth sx={{ mt: 2 }}>View Detailed Reports</Button>
        </CardContent>
    );
};


// --- Main Reports Dashboard Component ---
const ReportsDashboard = () => {
    const theme = useTheme();
    const [timeframe, setTimeframe] = useState('Last 30 Days');
    const [reportType, setReportType] = useState('single');
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [frequency, setFrequency] = useState('monthly');

    const handleDownload = (reportName) => {
        console.log(`Downloading report: ${reportName}`);
        alert(`Downloading ${reportName}...`);
    };

    // FIX 2: This component was defined right before the return statement,
    // which caused the function to implicitly return here instead of the main UI.
    // It's moved outside or defined as a function/const within the component logic block.
    // Keeping it here is fine as long as it does not precede the main return statement.
    const ReportCardWrapper = ({ title, icon: Icon, children, sx = {} }) => (
        <Card 
            elevation={8} 
            sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                borderRadius: 3,
                transition: 'transform 0.3s',
                '&:hover': { 
                    transform: 'translateY(-3px)', 
                    boxShadow: theme.shadows[15] 
                },
                ...sx
            }}
        >
            <CardContent sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={800} color="primary.dark">
                        {title}
                    </Typography>
                    <Icon color="primary" sx={{ fontSize: 32 }} />
                </Box>
            </CardContent>
            <Box sx={{ flexGrow: 1, p: 3 }}>
                {children}
            </Box>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'right' }}>
                <Button size="small" onClick={() => handleDownload(title)} startIcon={<DownloadIcon />} color="primary">
                    Export Data
                </Button>
            </Box>
        </Card>
    );

    // FIX 2: The main render block needs to be returned. 
    // The previous code had the main return block placed *after* the ReportCardWrapper definition, 
    // but the presence of the ReportCardWrapper definition itself was not the issue, 
    // it was the comment block below it (`// --- RENDER COMPONENT ---`) and its subsequent return statement.
    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header and Controls */}
            <Box 
                sx={{ 
                    mb: 4, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    opacity: 0,
                    transform: 'translateY(-10px)',
                    animation: 'headerSlide 0.8s ease-out forwards',
                    '@keyframes headerSlide': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    fontWeight={700} 
                    color="primary.dark"
                    sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
                >
                    <TrendIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'inherit' }} />
                    Performance & Analytics Dashboard
                </Typography>
                <Box display="flex" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Timeframe</InputLabel>
                        <Select
                            value={timeframe}
                            label="Timeframe"
                            onChange={(e) => setTimeframe(e.target.value)}
                        >
                            {['Last 7 Days', 'Last 30 Days', 'Last Quarter', 'Last Year'].map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh Data">
                        <IconButton onClick={() => alert('Refreshing data...')} color="primary" sx={{ border: `1px solid ${theme.palette.primary.main}` }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Report Configuration Panel */}
            <Paper elevation={4} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>Report Configuration</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Report Type</InputLabel>
                            <Select value={reportType} label="Report Type" onChange={(e) => setReportType(e.target.value)}>
                                <MenuItem value="single">Single Employee</MenuItem>
                                <MenuItem value="multiple">Multiple Employees</MenuItem>
                                <MenuItem value="all">All Employees</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Frequency</InputLabel>
                            <Select value={frequency} label="Frequency" onChange={(e) => setFrequency(e.target.value)}>
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button variant="contained" fullWidth sx={{ height: '40px' }} startIcon={<DownloadIcon />}>
                            Generate Report
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Top Metrics Cards (Small, High-Density) */}
            <Grid 
                container 
                spacing={3} 
                sx={{ 
                    mb: 4, 
                    // Animation: Slide-in from top
                    opacity: 0,
                    transform: 'translateY(20px)',
                    animation: 'gridSlideIn 1s ease-out 0.2s forwards',
                    '@keyframes gridSlideIn': { 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                {/* Metric 1: Report Generation Status */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={6} sx={{ p: 2, borderRadius: 3, borderLeft: `5px solid ${theme.palette.success.main}` }}>
                        <Typography variant="subtitle2" color="text.secondary">Report Success Rate</Typography>
                        <Typography variant="h4" fontWeight={800} color="success.main" sx={{ my: 1 }}>98.5%</Typography>
                        <Chip label="Up to Date" size="small" icon={<RefreshIcon fontSize="small" />} color="success" />
                    </Card>
                </Grid>
                {/* Metric 2: Top Active Department */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={6} sx={{ p: 2, borderRadius: 3, borderLeft: `5px solid ${theme.palette.info.main}` }}>
                        <Typography variant="subtitle2" color="text.secondary">Highest Engagement</Typography>
                        <Typography variant="h4" fontWeight={800} color="info.main" sx={{ my: 1 }}>Engineering</Typography>
                        <Chip label="+12% activity" size="small" icon={<TrendIcon fontSize="small" />} color="info" />
                    </Card>
                </Grid>
                {/* Metric 3: Budget Variance */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={6} sx={{ p: 2, borderRadius: 3, borderLeft: `5px solid ${theme.palette.error.main}` }}>
                        <Typography variant="subtitle2" color="text.secondary">Budget Variance YTD</Typography>
                        <Typography variant="h4" fontWeight={800} color="error.main" sx={{ my: 1 }}>{mockReportData.financeSummary.variance}%</Typography>
                        <Chip label="Requires Review" size="small" icon={<MoneyIcon fontSize="small" />} color="error" />
                    </Card>
                </Grid>
                {/* Metric 4: Scheduled Reports */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={6} sx={{ p: 2, borderRadius: 3, borderLeft: `5px solid ${theme.palette.warning.main}` }}>
                        <Typography variant="subtitle2" color="text.secondary">Next Scheduled Run</Typography>
                        <Typography variant="h4" fontWeight={800} color="warning.main" sx={{ my: 1 }}>12 Hours</Typography>
                        <Chip label="Daily Snapshot" size="small" icon={<ScheduleIcon fontSize="small" />} color="warning" />
                    </Card>
                </Grid>
            </Grid>
            
            {/* Main Report Cards (Flexible Layout) */}
            <Grid container spacing={4}>
                {/* Card 1: Headcount Distribution (Simulated Chart) */}
                <Grid item xs={12} lg={4}>
                    <ReportCardWrapper title="Headcount Distribution" icon={PeopleIcon}>
                        <EmployeeDistributionChart data={mockReportData.employeeMetrics} theme={theme} />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                            Total Active Employees: 3,900. Data calculated from HRIS sync.
                        </Typography>
                    </ReportCardWrapper>
                </Grid>

                {/* Card 2: Financial Utilization Meter */}
                <Grid item xs={12} lg={4}>
                    <ReportCardWrapper title="Q4 Budget Utilization" icon={MoneyIcon}>
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="h2" fontWeight={800} color={mockReportData.financeSummary.budgetUtilization > 70 ? 'error.main' : 'success.main'}>
                                {mockReportData.financeSummary.budgetUtilization}%
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary" mb={2}>
                                Budget Used YTD (Goal: 80%)
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={mockReportData.financeSummary.budgetUtilization} 
                                color={mockReportData.financeSummary.budgetUtilization > 70 ? 'error' : 'success'}
                                sx={{ height: 15, borderRadius: 8, my: 2 }}
                            />
                            <Button variant="outlined" startIcon={<TrendIcon />} size="small">
                                Analyze Financial Deep Dive
                            </Button>
                        </Box>
                    </ReportCardWrapper>
                </Grid>

                {/* Card 3: Training Progress */}
                <Grid item xs={12} lg={4}>
                    <Card elevation={8} sx={{ height: '100%', borderRadius: 3, transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.shadows[15] } }}>
                        <TrainingProgressCard data={mockReportData.trainingCompletion} theme={theme} />
                        <Divider />
                        <Box sx={{ p: 1, textAlign: 'right' }}>
                            <Button size="small" onClick={() => handleDownload('Training Progress')} startIcon={<DownloadIcon />} color="primary">
                                Export Data
                            </Button>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Footer Action */}
            <Box sx={{ mt: 6, textAlign: 'center' }}>
                <Button variant="contained" size="large" startIcon={<ScheduleIcon />} sx={{ px: 5, py: 1.5, fontWeight: 700 }}>
                    Schedule New Custom Report
                </Button>
            </Box>
        </Container>
    );
};

export default ReportsDashboard;